/*
 * Session provenance for the Descope-backed MFA journey.
 *
 * Every MFA operation is authorized by a refresh token Descope itself issued (see
 * use-descope-web-auth.js). The auth token sitting in the user store, though, can come
 * from either of two places depending on the feature_DescopeAuthFlows flag and the entry
 * point the user arrived through:
 *
 *   - the Descope login flow, or registerViaDescope() -> a genuine Descope token
 *   - legacy Member Hub POST /login                   -> a Member Hub token
 *
 * Both token objects carry an `access_token` and a `refresh_token`, so a bare
 * `!!token.refresh_token` truthiness check — which the MFA surfaces previously used to
 * decide whether MFA was available at all — cannot tell them apart. It passes for a
 * Member Hub session, and the MFA UI then renders only to fail at its first Descope API
 * call. Decoding the issuer claim is what actually distinguishes the two.
 *
 * NOTE: decodeJwtPayload below intentionally mirrors the private helper of the same name
 * in use-descope-web-auth.js rather than importing it. That composable is the Descope
 * implementation proper and is deliberately left untouched by the MFA/Descope-flag
 * decoupling; a few duplicated lines are the cheaper trade against editing it.
 */

function decodeJwtPayload (token) {
  if (!token) return null

  const [, payload] = String(token).split('.')
  if (!payload) return null

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const normalizedPayload = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=')
    const json = typeof atob === 'function'
      ? atob(normalizedPayload)
      : Buffer.from(normalizedPayload, 'base64').toString('utf8')
    const parsed = JSON.parse(json)

    // JSON.parse happily returns arrays and primitives; neither is a valid JWT payload.
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null

    return parsed
  } catch {
    return null
  }
}

/*
 * True when `accessToken` is a session JWT minted by the configured Descope project.
 *
 * Descope stamps the project ID into `iss`, either bare ("P2abc...") or as the trailing
 * segment of an issuer URL ("https://api.descope.com/P2abc..."); both forms are accepted.
 * Fails closed on anything unparseable, missing, or from another issuer — a false
 * negative merely costs one extra Descope sign-in, whereas a false positive would send a
 * Member Hub token to Descope and surface a confusing mid-flow error to the user.
 */
export function isDescopeIssuedToken (accessToken, projectId) {
  if (!accessToken || !projectId) return false

  const issuer = decodeJwtPayload(accessToken)?.iss
  if (!issuer) return false

  const value = String(issuer)
  return value === projectId || value.split('/').filter(Boolean).pop() === projectId
}
